import React, { useState, useEffect } from 'react';
import ClientTokenGenerator from './components/ClientTokenGenerator';
import EbayListings from './components/EbayListings';

function EbayPage() {
  const [activeView, setActiveView] = useState('appToken'); // 'appToken', 'clientToken', or 'listings'
  const [clientToken, setClientToken] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);

  // Check for stored client token and load environment variables on component mount
  useEffect(() => {
    console.log('Component mounted, checking for environment variables');
    console.log('Environment variables available:', {
      REACT_APP_EBAY_CLIENT_ID: process.env.REACT_APP_EBAY_CLIENT_ID,
      REACT_APP_EBAY_CLIENT_SECRET: process.env.REACT_APP_EBAY_CLIENT_SECRET
    });
    
    // Load environment variables
    if (process.env.REACT_APP_EBAY_CLIENT_ID) {
      console.log('Setting client ID:', process.env.REACT_APP_EBAY_CLIENT_ID);
      setClientId(process.env.REACT_APP_EBAY_CLIENT_ID);
    } else {
      console.log('REACT_APP_EBAY_CLIENT_ID is not defined');
    }
    
    if (process.env.REACT_APP_EBAY_CLIENT_SECRET) {
      console.log('Setting client secret');
      setClientSecret(process.env.REACT_APP_EBAY_CLIENT_SECRET);
    } else {
      console.log('REACT_APP_EBAY_CLIENT_SECRET is not defined');
    }
    
    // Check for stored token
    const storedToken = sessionStorage.getItem("ebay_client_token");
    if (storedToken) {
      setClientToken(storedToken);
    }
  }, []);
  
  // Handle client token update from ClientTokenGenerator
  const handleClientTokenUpdate = (tokenData) => {
    setClientToken(tokenData.access_token);
    // Switch to listings view
    setActiveView('listings');
  };

  // Load from env vars if available
  const useEnvCredentials = () => {
    console.log('Environment variables:', {
      REACT_APP_EBAY_CLIENT_ID: process.env.REACT_APP_EBAY_CLIENT_ID,
      REACT_APP_EBAY_CLIENT_SECRET: process.env.REACT_APP_EBAY_CLIENT_SECRET
    });
    
    if (process.env.REACT_APP_EBAY_CLIENT_ID) {
      console.log('Setting client ID:', process.env.REACT_APP_EBAY_CLIENT_ID);
      setClientId(process.env.REACT_APP_EBAY_CLIENT_ID);
    } else {
      console.log('REACT_APP_EBAY_CLIENT_ID is not defined');
    }
    
    if (process.env.REACT_APP_EBAY_CLIENT_SECRET) {
      console.log('Setting client secret');
      setClientSecret(process.env.REACT_APP_EBAY_CLIENT_SECRET);
    } else {
      console.log('REACT_APP_EBAY_CLIENT_SECRET is not defined');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-md py-6">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">eBay Integration</h1>
          <div className="flex gap-4">
            <button 
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeView === 'appToken'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveView('appToken')}
            >
              Application Token
            </button>
            <button 
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeView === 'clientToken'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveView('clientToken')}
            >
              Client Token
            </button>
            {clientToken && (
              <button 
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeView === 'listings'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                onClick={() => setActiveView('listings')}
              >
                My eBay Listings
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8">
        {activeView === 'appToken' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-4">Application Token</h2>
            <p className="text-gray-600 mb-6">
              This generates an application access token using the Client Credentials grant flow.
              This token has limited privileges and can only access public eBay APIs.
            </p>
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Enter eBay API Credentials</h2>
              <div className="space-y-4">
                <div className="flex flex-col">
                  <label htmlFor="client-id" className="text-sm font-medium text-gray-700 mb-1">Client ID:</label>
                  <input
                    id="client-id"
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Your eBay Client ID"
                    className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="client-secret" className="text-sm font-medium text-gray-700 mb-1">Client Secret:</label>
                  <input
                    id="client-secret"
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Your eBay Client Secret"
                    className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-4 mt-6">
                  <button 
                    onClick={useEnvCredentials}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    Use Environment Variables
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeView === 'clientToken' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <ClientTokenGenerator onTokenUpdate={handleClientTokenUpdate} />
          </div>
        )}
        
        {activeView === 'listings' && clientToken && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-4">My eBay Listings</h2>
            <p className="text-gray-600 mb-6">
              Displaying your active eBay listings using the client token.
            </p>
            <EbayListings credentials={{ token: clientToken }} />
          </div>
        )}
      </main>
    </div>
  );
}

export default EbayPage;
